import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  BaseLayout,
  SkeletonLoader,
  Alert,
  Badge,
  ImagePreviewModal,
  Authorizor,
  Spinner,
} from '@/components';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '@/components/ui';
import {
  ALERT_TYPES,
  TOAST_TYPES,
  BADGE_TYPES,
} from '@/constants/common.constants';
import {
  SURVEY_ALERT_TYPES,
  SURVEY_ALERT_TYPE_LABELS,
} from '@/constants/survey.constants';
import { useToast } from '@/contexts/toast-context';
import { useAuth } from '@/hooks/use-auth';
import {
  getIncidentReportByUuid,
  markIncidentReportAsViewed,
  markIncidentReportAsUnviewed,
  downloadIncidentReportPdf,
} from '@/api/incident-report.api';
import type { IncidentReportResponse } from '@/types/incident-report.types';
import { SurveyType } from '@/types/surveys.types';
import ROLES from '@/constants/roles.constants';
import { formatSurveyDate, formatSurveyTime } from '@/lib/utils';

import './incident-reports-view-style.scss';

export const Route = createFileRoute('/incident-reports/$uuid/')({
  component: IncidentReportView,
});

function IncidentReportView() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [report, setReport] = useState<IncidentReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Fetch incident report data on component mount
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setIsLoading(true);
        const data = await getIncidentReportByUuid(uuid);
        setReport(data);
      } catch (err) {
        addToast(
          err instanceof Error ? err.message : 'Failed to load incident report',
          {
            type: TOAST_TYPES.ERROR,
          },
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [uuid]);

  const navigateToSurvey = (survey: { uuid: string; type: SurveyType }) => {
    if (survey.type === SurveyType.ROUTE_SURVEY) {
      navigate({ to: `/route-surveys/${survey.uuid}` });
    } else if (survey.type === SurveyType.INCIDENT_REPORT) {
      navigate({ to: `/incident-reports/${survey.uuid}` });
    } else if (survey.type === SurveyType.CUSTOMER_FEEDBACK) {
      navigate({ to: `/customer-feedbacks/${survey.uuid}` });
    }
  };

  const handlePreviousSurvey = () => {
    if (!report?.previousSurvey) return;
    navigateToSurvey(report.previousSurvey);
  };

  const handleNextSurvey = () => {
    if (!report?.nextSurvey) return;
    navigateToSurvey(report.nextSurvey);
  };

  const handleMarkAsViewed = async () => {
    if (!report || !user) return;

    try {
      await markIncidentReportAsViewed(uuid);
      addToast('Incident report marked as reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      // Update the local state to reflect the change
      setReport({
        ...report,
        viewed: true,
        viewedBy: {
          uuid: user.uuid,
          name: user.name,
        },
      });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark incident report as reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
    }
  };

  const handleMarkAsUnviewed = async () => {
    if (!report || !user) return;

    // Only allow unmarking if current user is the one who marked it as viewed
    if (report.viewedBy && report.viewedBy.uuid !== user.uuid) {
      addToast(
        'Only the person who marked this as reviewed can mark it as not reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
      return;
    }

    try {
      await markIncidentReportAsUnviewed(uuid);
      addToast('Incident report marked as not reviewed successfully', {
        type: TOAST_TYPES.SUCCESS,
      });
      // Update the local state to reflect the change
      setReport({ ...report, viewed: false, viewedBy: null });
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : 'Failed to mark incident report as not reviewed',
        {
          type: TOAST_TYPES.ERROR,
        },
      );
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadIncidentReportPdf(uuid);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to download PDF', {
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <BaseLayout
      header={{
        headerText: 'Incident Report',
        headerChildren:
          report && (report.previousSurvey || report.nextSurvey) ? (
            <div className="button-container-right incident-report-nav-buttons">
              {/* Navigation Buttons */}
              <Button
                variant="outline-secondary"
                size="small"
                onClick={handlePreviousSurvey}
                disabled={!report.previousSurvey}
              >
                <span>←</span>
                <span className="incident-report-nav-text">Previous</span>
              </Button>
              <Button
                variant="outline-secondary"
                size="small"
                onClick={handleNextSurvey}
                disabled={!report.nextSurvey}
              >
                <span className="incident-report-nav-text">Next</span>
                <span>→</span>
              </Button>
            </div>
          ) : undefined,
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: ['Incident Report'],
      }}
    >
      {isLoading ? (
        <SkeletonLoader count={10} height={30} />
      ) : !report ? null : (
        <div className="incident-report-view">
          {/* Feedback Overview Card */}
          <Card className="incident-report-view-overview-card">
            <CardContent>
              <div className="incident-report-view-overview">
                <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
                  <div className="incident-report-view-overview-item">
                    <div className="incident-report-view-overview-label">
                      Account
                    </div>
                    <div>{report.accountName}</div>
                  </div>
                </Authorizor>
                <div className="incident-report-view-overview-item">
                  <span className="incident-report-view-overview-label">
                    Alert Type
                  </span>
                  <Badge
                    text={SURVEY_ALERT_TYPE_LABELS[SURVEY_ALERT_TYPES.COMMENTS]}
                    type={BADGE_TYPES.WARNING}
                  />
                </div>
                <div className="incident-report-view-overview-item">
                  <span className="incident-report-view-overview-label">
                    Survey Date
                  </span>
                  <span>{formatSurveyDate(report.surveyDate)}</span>
                </div>
                <div className="incident-report-view-overview-item">
                  <span className="incident-report-view-overview-label">
                    Survey Time
                  </span>
                  <span>{formatSurveyTime(report.surveyDate)}</span>
                </div>
                <Authorizor
                  allowedRoles={[
                    ROLES.ADMINISTRATOR,
                    ROLES.EXECUTIVE,
                    ROLES.MANAGER,
                    ROLES.STAFF,
                  ]}
                >
                  <div className="incident-report-view-overview-item">
                    <span className="incident-report-view-overview-label">
                      Driver
                    </span>
                    <span>
                      <a
                        className="link"
                        href={`/organizations/${report.driver.uuid}`}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            e.stopPropagation();
                          } else {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate({
                              to: `/organizations/${report.driver.uuid}`,
                            });
                          }
                        }}
                      >
                        {report.driver.name}
                      </a>
                      {report.driver.status === 'deactivated' && (
                        <>
                          {' '}
                          <Badge text="Deactivated" type={BADGE_TYPES.DANGER} />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="incident-report-view-overview-item">
                    <span className="incident-report-view-overview-label">
                      Manager
                    </span>
                    <span>{report.manager}</span>
                  </div>
                </Authorizor>

                <div className="incident-report-view-overview-item">
                  <span className="incident-report-view-overview-label">
                    Category
                  </span>
                  <span>{report.aiCategory || 'N/A'}</span>
                </div>
                <div className="incident-report-view-overview-item">
                  <span className="incident-report-view-overview-label">
                    Reviewed
                  </span>
                  <span
                    className={`${
                      report.viewed
                        ? 'incident-report-view-overview-viewed'
                        : 'incident-report-view-overview-not-viewed'
                    }`}
                  >
                    {report.viewed && report.viewedBy
                      ? `Yes [${report.viewedBy.name}]`
                      : 'No'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Summary Card */}
          <Card className="incident-report-view-summary-card">
            <CardHeader>
              <CardTitle>Qluu Summary </CardTitle>
            </CardHeader>
            <CardContent>
              <p>{report.aiSummary || 'N/A'}</p>
            </CardContent>
          </Card>

          {/* Description Card */}
          <Card className="incident-report-view-description-card">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert
                type={ALERT_TYPES.WARNING}
                text={report.description}
                className="text-neutral-dark-1"
              />
            </CardContent>
          </Card>

          {/* Uploaded Photos Card */}
          <Card className="incident-report-view-photos-card">
            <CardHeader>
              <CardTitle>Uploaded Photos</CardTitle>
            </CardHeader>
            <CardContent>
              {report.imageUrls && report.imageUrls.length > 0 ? (
                <div className="incident-report-view-photos-grid">
                  {report.imageUrls.map((url, index) => (
                    <div
                      key={index}
                      className="incident-report-view-photo-item"
                      onClick={() => setPreviewImage(url)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={url} alt={`Incident photo ${index + 1}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-neutral-medium-3">
                  No photos uploaded for this incident
                </p>
              )}
            </CardContent>
          </Card>

          <div className="button-container-right">
            {/* Download PDF */}
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              <Button
                variant="outline-secondary"
                size="medium"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                {isDownloadingPdf && <Spinner size="small" />}
                Download PDF
              </Button>
            </Authorizor>
            {/* Mark as reviewed/ not reviewed */}
            <Authorizor
              allowedRoles={[
                ROLES.ADMINISTRATOR,
                ROLES.EXECUTIVE,
                ROLES.MANAGER,
                ROLES.STAFF,
              ]}
            >
              {report.canMarkAsViewed && (
                <>
                  {!report.viewed ? (
                    <Button
                      variant="basic-primary"
                      size="medium"
                      onClick={handleMarkAsViewed}
                    >
                      Mark as Reviewed
                    </Button>
                  ) : (
                    <Button
                      variant="outline-primary"
                      size="medium"
                      onClick={handleMarkAsUnviewed}
                      disabled={report.viewedBy?.uuid !== user?.uuid}
                    >
                      Mark as Not Reviewed
                    </Button>
                  )}
                </>
              )}
            </Authorizor>
          </div>
        </div>
      )}
      <ImagePreviewModal
        imageUrl={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </BaseLayout>
  );
}
